// mindmetal-app/web/src/pages/employee/DevTools.tsx
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Terminal,
  FileText,
  Beaker,
  User,
  Activity,
  Zap,
  AlertCircle,
  Clock,
} from 'lucide-react'
import { Card, CardContent } from '@personic/shared'
import { useSystemMetrics } from '@/hooks/useDevTools'

interface ToolCard {
  name: string
  description: string
  icon: React.ElementType
  href: string
  color: string
}

const tools: ToolCard[] = [
  {
    name: 'API Explorer',
    description: 'Test API endpoints with custom requests',
    icon: Terminal,
    href: '/employee/dev-tools/api',
    color: 'bg-blue-500',
  },
  {
    name: 'Log Viewer',
    description: 'Browse and search system logs',
    icon: FileText,
    href: '/employee/dev-tools/logs',
    color: 'bg-amber-500',
  },
  {
    name: 'Distillation Tester',
    description: 'Test personality distillation with sample content',
    icon: Beaker,
    href: '/employee/mindmetal?tab=distillation',
    color: 'bg-purple-500',
  },
  {
    name: 'Persona Tester',
    description: 'Generate and compare persona outputs',
    icon: User,
    href: '/employee/mindmetal?tab=persona',
    color: 'bg-copper-500',
  },
  {
    name: 'System Health',
    description: 'Monitor service status and metrics',
    icon: Activity,
    href: '/employee/health',
    color: 'bg-emerald-500',
  },
]

export default function DevTools() {
  const { data: metrics, isLoading: metricsLoading } = useSystemMetrics()

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-midnight-700 to-midnight-900 flex items-center justify-center">
          <Terminal className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-midnight-900">Developer Tools</h1>
          <p className="text-warm-500">Internal debugging and testing utilities</p>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">API Calls Today</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.apiCallsToday?.toLocaleString() || '0'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Errors Today</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.errorsToday || '0'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Avg Latency</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : `${metrics?.avgLatency || 0}ms`}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined" padding="sm">
          <CardContent className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-warm-500">Active Users</p>
              <p className="text-xl font-semibold text-midnight-800 font-mono">
                {metricsLoading ? '...' : metrics?.activeUsers || '0'}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tool Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool, index) => (
          <motion.div
            key={tool.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
          >
            <Link to={tool.href}>
              <Card
                variant="outlined"
                interactive
                padding="none"
                className="overflow-hidden group"
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${tool.color} text-white`}>
                      <tool.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-midnight-800 group-hover:text-copper-600 transition-colors">
                        {tool.name}
                      </h3>
                      <p className="text-sm text-warm-500 mt-1">{tool.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card variant="default">
          <CardContent className="p-6">
            <h3 className="font-semibold text-midnight-800 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/employee/dev-tools/api"
                className="px-4 py-2 bg-midnight-800 text-white rounded-lg text-sm hover:bg-midnight-700 transition-colors"
              >
                Test Health Endpoint
              </Link>
              <Link
                to="/employee/dev-tools/logs?level=ERROR"
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
              >
                View Recent Errors
              </Link>
              <Link
                to="/employee/mindmetal"
                className="px-4 py-2 bg-copper-500 text-white rounded-lg text-sm hover:bg-copper-600 transition-colors"
              >
                Run Test Distillation
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
